import * as React from 'react';
import { cn } from '../utils/cn';

const sizeStyles: Record<string, string> = {
  large: 'h-12 text-base',
  default: 'h-10 text-sm',
  small: 'h-8 text-xs',
};

// ─── Option ─────────────────────────────────────────────────────────────

export interface OptionProps {
  value: string | number;
  children: React.ReactNode;
  disabled?: boolean;
}

/** Select.Option compat — renders a native <option> */
export function Option({ value, children, disabled }: OptionProps) {
  return (
    <option value={value} disabled={disabled}>
      {children}
    </option>
  );
}

// ─── Select ─────────────────────────────────────────────────────────────

export interface SelectProps
  extends Omit<React.SelectHTMLAttributes<HTMLSelectElement>, 'size' | 'onChange'> {
  /** Select size */
  size?: 'large' | 'default' | 'small';
  /** Placeholder text */
  placeholder?: string;
  /** Controlled value */
  value?: string | number;
  /** Default value */
  defaultValue?: string | number;
  /** Change handler — receives the value directly (antd compat) */
  onChange?: (value: string | number, option?: any) => void;
  /** Allow clearing selection */
  allowClear?: boolean;
  /** Children: Option elements */
  children?: React.ReactNode;
}

const SelectBase = React.forwardRef<HTMLSelectElement, SelectProps>(
  (
    {
      className,
      size = 'default',
      placeholder,
      value,
      defaultValue,
      onChange,
      allowClear,
      disabled,
      style,
      children,
      ...props
    },
    ref
  ) => {
    const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
      if (onChange) {
        // antd Select onChange passes value directly, not event
        onChange(e.target.value);
      }
    };

    return (
      <div className={cn('relative', className)} style={style}>
        <select
          ref={ref}
          value={value}
          defaultValue={defaultValue}
          onChange={handleChange}
          disabled={disabled}
          className={cn(
            'w-full appearance-none bg-white text-foreground border border-input rounded-sm',
            'px-3 pr-8 font-body',
            'focus:outline-none focus:ring-2 focus:ring-ring focus:border-primary',
            'disabled:opacity-40 disabled:cursor-not-allowed',
            'transition-colors duration-fast ease-smooth',
            sizeStyles[size]
          )}
          {...props}
        >
          {placeholder && (
            <option value="" disabled>
              {placeholder}
            </option>
          )}
          {children}
        </select>
        {/* Dropdown chevron */}
        <svg
          className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-muted"
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </div>
    );
  }
);
SelectBase.displayName = 'Select';

// Compound component for antd compat: Select.Option
export const Select = Object.assign(SelectBase, { Option });
